import PaySonicLogo from "@assets/logos/PaySonic.svg";
import TyltLogo from "@assets/logos/tylt_logo.png";
import LogoutSlidingPanel from "@components/sliders/LogoutSlidingPanel";
import webSocketService from "@services/websocket/WebsocketServices";
import { useEffect, useMemo, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import secureLocalStorage from "react-secure-storage";
import {
  isPathAllowedForPaysonic,
  isPathAllowedForPrimeBrazilOnly,
  isPaysonicHost,
  isPrimeBrazilOnlyHost,
} from "src/config/deploymentConfig";
import {
  ADMIN_NAV,
  filterNav,
  isNavItemActive,
  NavItem,
  NavSection,
} from "./adminNav";

const SIDEBAR_WIDTH = "w-[176px]";

const visibleSections = (): NavSection[] => {
  if (isPrimeBrazilOnlyHost()) {
    return ADMIN_NAV.map((section) => ({
      ...section,
      items: section.items.filter((item) =>
        isPathAllowedForPrimeBrazilOnly(item.path),
      ),
    })).filter((section) => section.items.length > 0);
  }

  if (isPaysonicHost()) {
    return ADMIN_NAV.map((section) => ({
      ...section,
      items: section.items.filter((item) => isPathAllowedForPaysonic(item.path)),
    })).filter((section) => section.items.length > 0);
  }

  return ADMIN_NAV;
};

function NavButton({
  item,
  active,
  showAlert,
  onClick,
}: {
  item: NavItem;
  active: boolean;
  showAlert?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13px] font-medium transition-colors ${
        active
          ? "bg-[#E8F6FC] text-[#2436C7]"
          : "text-[#3A4158] hover:bg-[#F4F6F8]"
      }`}
    >
      <span className="material-symbols-outlined text-[18px] leading-none">
        {item.icon}
      </span>
      <span className="min-w-0 flex-1 truncate">{item.label}</span>
      {showAlert && (
        <span className="h-2 w-2 shrink-0 rounded-full bg-[#D61C1C]" />
      )}
    </button>
  );
}

function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLogout, setIsLogout] = useState(false);
  const [query, setQuery] = useState("");
  const [primeDisputeNotification, setPrimeDisputeNotification] =
    useState(false);
  const currentPath = location.pathname || "/";
  const pathRef = useRef(currentPath);
  pathRef.current = currentPath;
  const isPrimeBrazilOnly = isPrimeBrazilOnlyHost();
  const isPaysonic = isPaysonicHost();

  useEffect(() => {
    try {
      webSocketService.onMessage("admin_trade_update_dispute", true, () => {
        if (!pathRef.current.startsWith("/prime-dispute-resolution")) {
          setPrimeDisputeNotification(true);
        }
      });
    } catch (error) {
      console.error("WebSocket initialization error:", error);
    }
  }, [webSocketService.protectedSocket?.connected]);

  const sections = useMemo(
    () => filterNav(visibleSections(), query),
    [query],
  );

  const openItem = (item: NavItem) => {
    if (item.notify === "disputes") setPrimeDisputeNotification(false);
    navigate(item.path);
  };

  return (
    <div className="flex h-screen min-w-screen overflow-hidden bg-[#F7F8FA]">
      <aside
        className={`sticky top-0 flex h-screen ${SIDEBAR_WIDTH} shrink-0 flex-col border-r border-border-subtle bg-[#FFFFFF] px-3 py-4`}
      >
        <div className="mb-4 px-2">
          {isPrimeBrazilOnly ? (
            <div className="h-8" />
          ) : (
            <img
              src={isPaysonic ? PaySonicLogo : TyltLogo}
              alt="Logo"
              className={`object-contain ${isPaysonic ? "h-6 w-auto" : "h-8 w-auto"}`}
            />
          )}
        </div>

        <label className="relative mb-4 block">
          <span className="material-symbols-outlined pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[16px] text-[#686E92]">
            search
          </span>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search"
            className="h-8 w-full rounded-lg border border-[#D7DEE8] bg-[#F7F8FA] pl-8 pr-2 text-[12px] font-medium text-[#15182C] outline-none placeholder:text-[#686E92] focus:border-[#7EC8EA] focus:bg-[#FFFFFF]"
          />
        </label>

        <nav className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto">
          {sections.length === 0 ? (
            <p className="px-2.5 text-[12px] text-text-subtle">
              No pages match “{query.trim()}”.
            </p>
          ) : (
            sections.map((section) => (
              <div key={section.title}>
                <p className="px-2.5 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-text-subtle">
                  {section.title}
                </p>
                <div className="flex flex-col gap-0.5">
                  {section.items.map((item) => (
                    <NavButton
                      key={item.path}
                      item={item}
                      active={isNavItemActive(currentPath, item.path)}
                      showAlert={
                        item.notify === "disputes" &&
                        primeDisputeNotification &&
                        !currentPath.startsWith("/prime-dispute-resolution")
                      }
                      onClick={() => openItem(item)}
                    />
                  ))}
                </div>
              </div>
            ))
          )}
        </nav>

        <button
          type="button"
          onClick={() => setIsLogout(true)}
          className="mt-3 flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] font-medium text-[#3A4158] transition-colors hover:bg-[#F4F6F8]"
        >
          <span className="material-symbols-outlined text-[18px] leading-none">
            logout
          </span>
          Logout
        </button>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden bg-bg-page-body">
        <div className="flex-1 overflow-auto">
          <LogoutSlidingPanel
            isOpen={isLogout}
            onClose={() => setIsLogout(false)}
            onClickConfirm={() => {
              secureLocalStorage.clear();
              navigate("/login");
            }}
            sliderHeading="Logout"
          />
          <Outlet />
        </div>
      </div>
    </div>
  );
}

export default Layout;
